import React from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    Stack,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { CheckCircleOutline, WorkspacePremium } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import GeneralInfoBox from "./GeneralInfoBox";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";


export default function PricingPlanComparisonPopup({ onClose, onContinueFreemium }) {
    const { t } = useTranslation();

    const freemiumFeatures = [t("General Scope 1 & 2"), t("Limited Access Features")];

    const premiumFeatures = [
        t("Full Scope 1, 2"),
        t("All 15 Scope 3 Categories"),
        t("Full Access Report"),
        t("Advanced Scenario Modelling"),
        t("Reduction & Removal Action"),
        t("Audit-ready Carbon Reporting"),
        t("Analytics & Performance Tracking"),
    ];

    const FeatureList = ({ items, highlight = false }) => (
        <List dense disablePadding sx={{ m: 0 }}>
            {items.map((label, idx) => (
                <ListItem
                    key={idx}
                    disableGutters
                    sx={{ py: 0.3, alignItems: "flex-start" }}
                >
                    <ListItemIcon sx={{ minWidth: 28, mt: "2px" }}>
                        <CheckCircleOutline fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                            sx: {
                                fontSize: "0.95rem",
                                lineHeight: 1.35,
                                fontWeight: highlight ? 600 : 500,
                            },
                        }}
                    />
                </ListItem>
            ))}
        </List>
    );

    const StatRow = ({ left, right }) => (
        <Stack direction="row" justifyContent="space-between" sx={{ fontSize: "0.85rem", opacity: 0.8 }}>
            <span>{left}</span>
            <span style={{ fontWeight: 700 }}>{right}</span>
        </Stack>
    );

    return (
        <GeneralInfoBox
            title="Choose your plan"
            body="Unlock more reporting and workflow features with Premium."
            onClose={onClose}
            showClose
            maxWidth="52rem"
            style={{ padding: 24 }}
        // footer={
        //     <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
        //         <Button variant="outlined" size="large" onClick={onClose}>
        //             {t("Maybe later")}
        //         </Button>
        //     </Stack>
        // }
        >
            <Box
                sx={{
                    textAlign: "left",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                    mt: 2,
                }}
            >
                {/* Freemium */}
                <Box
                    sx={{
                        border: "1px solid #E6F0EC",
                        borderRadius: 2,
                        background: "#FFFFFF",
                        p: 2.25,
                        minHeight: 320,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Stack spacing={1.2} sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography sx={{ fontWeight: 900, fontSize: "1.05rem" }}>
                                    {t("Freemium")}
                                </Typography>
                                <Typography sx={{ fontSize: "0.85rem", opacity: 0.75 }}>
                                    {t("Basic tracking to get started")}
                                </Typography>
                            </Box>
                            {/* <Chip label={t("Free")} size="small" variant="outlined" sx={{ fontWeight: 700 }} /> */}
                        </Stack>

                        <Divider sx={{ borderColor: "#E6F0EC" }} />

                        <Stack spacing={0.6}>
                            <StatRow left={t("Analytics")} right={t("Limited")} />
                        </Stack>

                        <Divider sx={{ borderColor: "#E6F0EC" }} />

                        <FeatureList items={freemiumFeatures} />

                        <Box sx={{ flex: 1 }} />

                        <Button
                            variant="outlined"
                            fullWidth
                            size="large"
                            onClick={onContinueFreemium || onClose}
                        >
                            {t("Continue with Freemium")}
                        </Button>
                    </Stack>
                </Box>

                {/* Premium */}
                <Box
                    sx={{
                        border: "1px solid #BFE3D3",
                        borderRadius: 2,
                        background:
                            "linear-gradient(180deg, rgba(232,250,244,0.95) 0%, rgba(255,255,255,0.98) 100%)",
                        p: 2.25,
                        minHeight: 320,
                        boxShadow: "0 14px 30px rgba(0,0,0,0.08)",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >

                    <Stack spacing={1.2} sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <WorkspacePremium fontSize="small" />
                                    <Typography sx={{ fontWeight: 900, fontSize: "1.05rem" }}>
                                        {t("Premium")}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ fontSize: "0.85rem", opacity: 0.75 }}>
                                    {t("Best for audits, reports, and scale")}
                                </Typography>
                            </Box>

                            {/* <Typography
                                sx={{
                                    fontWeight: 900,
                                    fontSize: "0.95rem",
                                    opacity: 0.85,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {t("Lorem / month")}
                            </Typography> */}
                        </Stack>

                        <Divider sx={{ borderColor: "#BFE3D3" }} />

                        <Stack spacing={0.6}>
                            <StatRow left={t("Reports")} right={t("Unlimited")} />
                            <StatRow left={t("Support")} right={t("Priority")} />
                        </Stack>

                        <Divider sx={{ borderColor: "#BFE3D3" }} />

                        <FeatureList items={premiumFeatures} highlight />

                        <Box sx={{ flex: 1 }} />

                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            component="a"
                            href={"https://wa.me/6282228886906?text=Hello%20Trucarbon,%20I%20want%20to%20upgrade%20to%20Premium."}
                            target="_blank"
                            rel="noreferrer"
                            startIcon={<WhatsAppIcon />}
                        >
                            {t("Upgrade now")}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </GeneralInfoBox>
    );
}
